import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type {
  CatalogSku,
  OrgMemberSummary,
  OrgModelPermissionMode,
  OrgModelPermissionWriteEntry,
} from '@nadoutong/contracts';
import { PlatformApiError } from '../../api/client.ts';
import { useCatalog } from '../../api/useCatalog.ts';
import {
  useOrgMemberModelPermissions,
  useReplaceOrgMemberModelPermissions,
} from '../../api/useOrgs.ts';
import { Modal } from '../../components/Modal.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { StatusChip } from '../../components/StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Input, cx } from '../../ui/index.ts';

/**
 * Per-member model permission editor (drawer) — Field Notes rebuild
 * (epic #950 PR-4): inherit / all / custom modes with a single
 * default-model radio, save via the replace endpoint.
 *
 * Since epic #898 P6 (#913) the checklist is SKU-grain: pickable items
 * come from the marketplace catalog (`/v1/catalog/me` `skus[]`, grouped
 * by namespace) and saves WRITE `skuId`-keyed entries. The platform
 * migration folds historical grants into authoritative SKU identities,
 * so this editor never reads or writes channel-keyed permission rows.
 */

type TranslateFn = ReturnType<typeof useT>;

interface PermissionSkuItem {
  readonly skuId: string;
  readonly namespaceSlug: string;
  readonly namespaceName: string;
  readonly skuName: string;
  readonly vendor: string;
  readonly model: string;
  readonly modelLabel: string;
}

interface PermissionNamespaceGroup {
  readonly slug: string;
  readonly displayName: string;
  readonly items: readonly PermissionSkuItem[];
}

interface MemberModelPermissionsModalProps {
  readonly orgId: string;
  readonly member: OrgMemberSummary;
  readonly onClose: () => void;
}

export function MemberModelPermissionsModal({
  orgId,
  member,
  onClose,
}: MemberModelPermissionsModalProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const catalogQ = useCatalog();
  const permissionsQ = useOrgMemberModelPermissions(orgId, member.accountId, true);
  const replaceM = useReplaceOrgMemberModelPermissions();
  const [mode, setMode] = useState<OrgModelPermissionMode>('inherit');
  const [selectedSkuIds, setSelectedSkuIds] = useState<ReadonlySet<string>>(() => new Set());
  const [defaultSkuId, setDefaultSkuId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const groups = useMemo(
    () => buildPermissionNamespaceGroups(catalogQ.data?.skus ?? []),
    [catalogQ.data?.skus],
  );
  const catalogItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (normalizedSearch.length === 0) {
      return groups;
    }
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => permissionItemMatches(item, normalizedSearch)),
      }))
      .filter(
        (group) =>
          group.items.length > 0 ||
          group.displayName.toLowerCase().includes(normalizedSearch) ||
          group.slug.toLowerCase().includes(normalizedSearch),
      );
  }, [groups, normalizedSearch]);
  const defaultItem =
    defaultSkuId === null ? undefined : catalogItems.find((item) => item.skuId === defaultSkuId);
  const directPermissions = permissionsQ.data?.permissions ?? [];
  const effectivePermissions = permissionsQ.data?.effectivePermissions ?? directPermissions;
  const inheritedLabel = formatModelPermissionInheritance(
    permissionsQ.data?.inheritedFrom ?? null,
    t,
  );
  const effectiveCount =
    mode === 'all'
      ? catalogItems.length
      : mode === 'custom'
        ? selectedSkuIds.size
        : permissionsQ.data?.effectiveMode === 'all'
          ? catalogItems.length
          : effectivePermissions.length;
  const isBusy = permissionsQ.isLoading || catalogQ.isLoading || replaceM.isPending;
  const loadErrorMessage =
    catalogQ.isError || permissionsQ.isError
      ? catalogQ.isError
        ? formatApiError(catalogQ.error, t('settings.model_permissions.error'))
        : formatApiError(permissionsQ.error, t('settings.model_permissions.error'))
      : null;
  const canSave =
    !isBusy && permissionsQ.data !== undefined && !catalogQ.isError && !permissionsQ.isError;

  useEffect(() => {
    if (permissionsQ.data === undefined) {
      return;
    }
    const loadedPermissions = permissionsQ.data.permissions;
    const nextMode =
      permissionsQ.data.mode ??
      (loadedPermissions.length > 0 ? 'custom' : member.modelPermissionMode ?? 'inherit');
    setMode(nextMode);
    const nextSelected = new Set(
      loadedPermissions.map((permission) => permission.skuId),
    );
    setSelectedSkuIds(nextSelected);
    const nextDefault = loadedPermissions.find(
      (permission) => permission.isDefault,
    );
    const nextDefaultSkuId = nextDefault?.skuId ?? null;
    setDefaultSkuId(
      nextDefaultSkuId !== null && nextSelected.has(nextDefaultSkuId) ? nextDefaultSkuId : null,
    );
    // The factory resets local editor state whenever the loaded
    // permissions change for the focused member; mirroring its
    // dependency shape keeps the reset semantics identical.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.accountId, permissionsQ.data]);

  function toggleSelection(item: PermissionSkuItem): void {
    setMode('custom');
    setSelectedSkuIds((current) => {
      const next = new Set(current);
      if (next.has(item.skuId)) {
        next.delete(item.skuId);
        if (defaultSkuId === item.skuId) {
          setDefaultSkuId(null);
        }
      } else {
        next.add(item.skuId);
      }
      return next;
    });
  }

  function toggleGroup(group: PermissionNamespaceGroup): void {
    setMode('custom');
    const groupIds = group.items.map((item) => item.skuId);
    const allSelected = groupIds.every((skuId) => selectedSkuIds.has(skuId));
    const shouldClearDefault =
      allSelected && defaultSkuId !== null && groupIds.includes(defaultSkuId);
    setSelectedSkuIds((current) => {
      const next = new Set(current);
      for (const skuId of groupIds) {
        if (allSelected) {
          next.delete(skuId);
        } else {
          next.add(skuId);
        }
      }
      return next;
    });
    if (shouldClearDefault) {
      setDefaultSkuId(null);
    }
  }

  function retryLoad(): void {
    void catalogQ.refetch();
    void permissionsQ.refetch();
  }

  async function handleSave(): Promise<void> {
    setError(null);
    const skuEntries: OrgModelPermissionWriteEntry[] = catalogItems
      .filter((item) => selectedSkuIds.has(item.skuId))
      .map((item) => ({
        skuId: item.skuId,
        isDefault: item.skuId === defaultSkuId,
      }));
    try {
      await replaceM.mutateAsync({
        orgId,
        accountId: member.accountId,
        body: {
          mode,
          permissions: mode === 'custom' ? skuEntries : [],
        },
      });
      toast.success(t('settings.model_permissions.toast.saved'));
      onClose();
    } catch (err) {
      const message =
        err instanceof PlatformApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!replaceM.isPending) onClose();
      }}
      titleId="member-model-permissions-title"
      testId="member-model-permissions-modal"
      variant="drawer"
    >
      <div className="flex h-full flex-col bg-card">
        <header className="border-b border-line px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="font-mono text-mono-label uppercase text-rust-text">
                  {t('settings.model_permissions.eyebrow')}
                </span>
                <StatusChip
                  variant={member.status === 'active' ? 'ok' : 'warning'}
                  label={member.status}
                />
              </div>
              <h2
                id="member-model-permissions-title"
                className="m-0 font-display text-title text-ink"
              >
                {t('settings.model_permissions.title')}
              </h2>
              <p className="m-0 mt-1 text-caption text-ink-low">
                {t('settings.model_permissions.subtitle', {
                  member: member.displayName ?? member.email,
                })}
              </p>
              <p className="m-0 mt-1 font-mono text-caption text-ink-mid">{member.email}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={replaceM.isPending}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-raised text-ink-mid transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40 disabled:pointer-events-none disabled:opacity-40"
              aria-label={t('settings.model_permissions.close')}
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded border border-line-soft bg-raised px-3 py-1.5 font-mono text-caption text-ink">
              {t('settings.model_permissions.authorized_count', {
                count: String(effectiveCount),
              })}
            </span>
            <span className="rounded border border-line-soft bg-raised px-3 py-1.5 font-mono text-caption text-ink">
              {t(`settings.model_permissions.mode.${mode}`)}
            </span>
            {mode === 'inherit' ? (
              <span className="rounded border border-line-soft bg-raised px-3 py-1.5 font-mono text-caption text-ink">
                {t('settings.model_permissions.inherited_from', { source: inheritedLabel })}
              </span>
            ) : null}
            <span className="rounded border border-line-soft bg-raised px-3 py-1.5 font-mono text-caption text-ink">
              {t('settings.model_permissions.namespace_count', { count: String(groups.length) })}
            </span>
            <span className="rounded border border-line-soft bg-raised px-3 py-1.5 font-mono text-caption text-ink">
              {t('settings.model_permissions.default_model', { model: defaultItem?.model ?? '—' })}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(['inherit', 'all', 'custom'] as const).map((candidate) => {
              const active = mode === candidate;
              return (
                <button
                  key={candidate}
                  type="button"
                  onClick={() => {
                    setMode(candidate);
                    setError(null);
                  }}
                  disabled={isBusy}
                  className={cx(
                    'rounded-lg border px-3 py-2 text-left text-label transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40 disabled:pointer-events-none disabled:opacity-40',
                    active
                      ? 'border-forest bg-forest-tint text-ink'
                      : 'border-line bg-raised text-ink-mid hover:bg-active/60 hover:text-ink',
                  )}
                  data-testid={`member-model-permissions-mode-${candidate}`}
                >
                  <span className="block">{t(`settings.model_permissions.mode.${candidate}`)}</span>
                  <span className="mt-1 block text-caption font-normal text-ink-low">
                    {t(`settings.model_permissions.mode_help.${candidate}`)}
                  </span>
                </button>
              );
            })}
          </div>
          {loadErrorMessage !== null ? (
            <div
              className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-tint px-4 py-3"
              role="alert"
              data-testid="member-model-permissions-load-error"
            >
              <span className="text-body text-bad">{loadErrorMessage}</span>
              <Button variant="secondary" size="sm" onClick={retryLoad}>
                {t('settings.model_permissions.retry')}
              </Button>
            </div>
          ) : null}
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isBusy && permissionsQ.data === undefined ? (
            <div className="flex flex-col gap-2" data-testid="member-model-permissions-loading">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : mode !== 'custom' ? (
            <div className="rounded-lg border border-line bg-raised p-4">
              <p className="m-0 text-body font-medium text-ink">
                {mode === 'all'
                  ? t('settings.model_permissions.all_models_active')
                  : t('settings.model_permissions.inherit_active', { source: inheritedLabel })}
              </p>
              <p className="m-0 mt-1 text-body text-ink-low">
                {mode === 'all'
                  ? t('settings.model_permissions.all_models_body')
                  : t('settings.model_permissions.inherit_body')}
              </p>
            </div>
          ) : groups.length === 0 ? (
            <p className="m-0 text-body text-ink-low" data-testid="member-model-permissions-empty">
              {t('settings.model_permissions.empty')}
            </p>
          ) : (
            <>
              <div className="mb-4">
                <label className="sr-only" htmlFor="member-model-permissions-search">
                  {t('settings.model_permissions.search_label')}
                </label>
                <Input
                  id="member-model-permissions-search"
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                  placeholder={t('settings.model_permissions.search_placeholder')}
                  data-testid="member-model-permissions-search"
                />
              </div>
              {filteredGroups.length === 0 && normalizedSearch.length > 0 ? (
                <p className="m-0 rounded-lg border border-line-soft bg-raised px-4 py-6 text-body text-ink-low">
                  {t('settings.model_permissions.no_search_results')}
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {filteredGroups.map((group) => {
                    const groupSelected = group.items.filter((item) =>
                      selectedSkuIds.has(item.skuId),
                    ).length;
                    const allSelected =
                      group.items.length > 0 && groupSelected === group.items.length;
                    return (
                      <section
                        key={group.slug}
                        className="overflow-hidden rounded-lg border border-line bg-card"
                        data-testid={`member-model-permission-namespace-${domToken(group.slug)}`}
                      >
                        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
                          <div className="min-w-0">
                            <h3 className="m-0 truncate text-heading text-ink">
                              {group.displayName}
                            </h3>
                            <p className="m-0 mt-1 truncate font-mono text-mono-label text-ink-low">
                              {group.slug}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-mono-label text-ink-low">
                              {groupSelected}/{group.items.length}{' '}
                              {t('settings.model_permissions.models')}
                            </span>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => toggleGroup(group)}
                              disabled={isBusy}
                            >
                              {allSelected
                                ? t('settings.model_permissions.clear_namespace')
                                : t('settings.model_permissions.select_namespace')}
                            </Button>
                          </div>
                        </header>
                        <div className="divide-y divide-line-soft">
                          {group.items.map((item) => {
                            const checked = selectedSkuIds.has(item.skuId);
                            return (
                              <div
                                key={item.skuId}
                                className="grid min-h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-2 transition-colors duration-150 hover:bg-active/40"
                              >
                                <label className="flex min-w-0 items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={isBusy || permissionsQ.data === undefined}
                                    onChange={() => toggleSelection(item)}
                                    data-testid={`member-model-permission-${domToken(item.skuId)}`}
                                    className="h-4 w-4 rounded border-line accent-forest disabled:opacity-40"
                                  />
                                  <span className="min-w-0">
                                    <span className="block truncate font-mono text-body text-ink">
                                      {item.model}
                                    </span>
                                    <span className="block truncate text-caption text-ink-low">
                                      {item.vendor} · {item.modelLabel}
                                    </span>
                                  </span>
                                </label>
                                <label className="flex items-center gap-2 text-caption text-ink-low">
                                  <input
                                    type="radio"
                                    name="member-model-default"
                                    checked={defaultSkuId === item.skuId}
                                    disabled={isBusy || !checked || permissionsQ.data === undefined}
                                    onChange={() => {
                                      setMode('custom');
                                      setSelectedSkuIds((current) =>
                                        new Set(current).add(item.skuId),
                                      );
                                      setDefaultSkuId(item.skuId);
                                    }}
                                    data-testid={`member-model-default-${domToken(item.skuId)}`}
                                    className="h-4 w-4 border-line accent-forest disabled:opacity-40"
                                  />
                                  {t('settings.model_permissions.default')}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>

        {error !== null ? (
          <div
            className="mx-6 mb-3 rounded-lg border border-bad-line bg-bad-tint px-4 py-3 text-body text-bad"
            role="alert"
            data-testid="member-model-permissions-error"
          >
            {error}
          </div>
        ) : null}

        <footer className="flex items-center justify-end gap-3 border-t border-line px-6 py-4">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={replaceM.isPending}
            data-testid="member-model-permissions-cancel"
          >
            {t('settings.model_permissions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSave();
            }}
            disabled={!canSave}
            data-testid="member-model-permissions-save"
          >
            {replaceM.isPending
              ? t('settings.model_permissions.saving')
              : t('settings.model_permissions.save')}
          </Button>
        </footer>
      </div>
    </Modal>
  );
}

function buildPermissionNamespaceGroups(
  skus: readonly CatalogSku[],
): readonly PermissionNamespaceGroup[] {
  const groups = new Map<string, { displayName: string; items: PermissionSkuItem[] }>();
  const seenSkuIds = new Set<string>();
  for (const sku of skus) {
    if (seenSkuIds.has(sku.sku_id)) continue;
    seenSkuIds.add(sku.sku_id);
    const group = groups.get(sku.namespace_slug) ?? {
      displayName: sku.namespace_display_name,
      items: [],
    };
    group.items.push({
      skuId: sku.sku_id,
      namespaceSlug: sku.namespace_slug,
      namespaceName: sku.namespace_display_name,
      skuName: sku.sku_name,
      vendor: sku.vendor,
      model: sku.model,
      modelLabel: sku.model_display_name ?? sku.model,
    });
    groups.set(sku.namespace_slug, group);
  }
  return [...groups.entries()]
    .map(([slug, group]) => ({
      slug,
      displayName: group.displayName,
      items: [...group.items].sort((a, b) => a.model.localeCompare(b.model)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function permissionItemMatches(item: PermissionSkuItem, normalizedSearch: string): boolean {
  return (
    item.namespaceSlug.toLowerCase().includes(normalizedSearch) ||
    item.namespaceName.toLowerCase().includes(normalizedSearch) ||
    item.skuName.toLowerCase().includes(normalizedSearch) ||
    item.vendor.toLowerCase().includes(normalizedSearch) ||
    item.model.toLowerCase().includes(normalizedSearch) ||
    item.modelLabel.toLowerCase().includes(normalizedSearch)
  );
}

function formatApiError(error: unknown, fallback: string): string {
  if (error instanceof PlatformApiError) {
    return `${error.code}: ${error.message}`;
  }
  const message = coerceErrorMessage(error);
  return message.length > 0 ? message : fallback;
}

function formatModelPermissionInheritance(
  inheritedFrom: {
    readonly orgUnitName: string | null;
    readonly orgUnitPath: string | null;
    readonly source: string;
  } | null,
  t: TranslateFn,
): string {
  if (inheritedFrom === null || inheritedFrom.source === 'global') {
    return t('settings.model_permissions.global_source');
  }
  return (
    inheritedFrom.orgUnitPath ??
    inheritedFrom.orgUnitName ??
    t('settings.model_permissions.global_source')
  );
}

function domToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'item';
}
